import {
  AlertButton,
  IonAlert,
  IonButton,
  IonFab,
  IonFabButton,
  IonFabList,
  IonIcon,
  IonPage,
  IonRouterOutlet,
  IonSegment,
  IonSegmentButton,
  IonTitle,
  IonicSlides,
} from '@ionic/react';
import '@ionic/react/css/ionic-swiper.css';
import { helpCircleOutline } from 'ionicons/icons';
import {
  ReactElement,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router';
import { useMeasure } from 'react-use';
import { Swiper as SwiperInterface } from 'swiper';
import 'swiper/css';
import 'swiper/css/autoplay';
import 'swiper/css/keyboard';
import 'swiper/css/pagination';
import 'swiper/css/scrollbar';
import 'swiper/css/zoom';
import {
  Autoplay,
  Keyboard,
  Pagination,
  Scrollbar,
  Zoom,
} from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import {
  AuthorizationProvider,
  useHasPermission,
} from '../../api/authorization.provider';
import {
  Machine,
  ScratchpadApp,
  useGetMachineQuery,
  useListScratchpadAppsQuery,
  useSubscribeToSingleMachineStateSubscription,
} from '../../api/graphql/generated';
import { useMachineAlerts } from '../../api/hooks/binding/machine-alert.binding';
import { TeamRoleProvider } from '../../api/hooks/role.hooks';
import { LoadingRetry } from '../../components/graphql/LoadingRetry';
import { BackButtonProvider } from '../../components/header/back-button';
import { newScratchpad } from '../../components/icons/new-scratch-pad';
import { MachineAuthorizationProvider } from '../../components/machine/machine-authorization-provider';
import { MachineConfig } from '../../components/machine/machine-config';
import { MachineProgramStatus } from '../../components/machine/machine-program-status';
import { ScratchpadPlaylistComponent } from '../../components/machine/playlist/scratchpad-playlist';
import { ScratchpadList } from '../../components/machine/scratchpad/scratchpad-list';
import {
  SupportOpenSource,
  useSupportContext,
} from '../../components/support/support.context';
import {
  IonContentWithContext,
  useIonContentContext,
} from '../../components/ui/ion-content-with-context';
import { MachineContextProvider } from '../../pages/machine/machine-context-provider';
import { MachineDetailsPage } from '../../pages/machine/machine-details.page';
import { MachineScratchpadPage } from '../../pages/machine/machine-scratchpad.page';
import { Page } from '../../pages/page';
import { useBeaconRouter } from '../../route/route.hooks';
import { ScratchpadAppIcon } from '../../scratchpad/app-slugs';
import { useLocationHash, usePrevious } from '../../util/hooks';
import { useGetDirectConnectCachedMachine } from '../cache/direct-connect-machine-cache';
import { useDirectConnectBackButtonListener } from '../component/direct-connect-back-button-listener';
import { DirectConnectGate } from '../gate/direct-connect-gate';
import { DirectConnectPageHeader } from '../header/direct-connect-page-header';
import '../../pages/machine/machine.page.css';

interface RouteParams {
  machineId: string;
}
interface MachinePageInternalProps {
  teamId: string;
  machineId: string;
}

interface MachineTopTab {
  hash: string;
  label: string;
  component: ReactElement;
}

const DirectConnectMachinePage = () => {
  const params = useParams<RouteParams>();
  const { machineId } = params;

  const machine = useGetDirectConnectCachedMachine(machineId);
  const showMachineNotFound = !machine;

  return (
    <BackButtonProvider>
      <>
        {machine && (
          <DirectConnectGate serialNumber={machine.serialNumber}>
            <MachinePages teamId={machine.teamId} machineId={machine.id} />
          </DirectConnectGate>
        )}
        {showMachineNotFound && <DirectConnectMachineNotFound />}
      </>
    </BackButtonProvider>
  );
};

const DirectConnectMachineNotFound = () => {
  const { i18n } = useTranslation();
  const { builder } = useBeaconRouter();
  const { replace } = useHistory();

  const buttons = useMemo<AlertButton[]>(() => {
    return [
      {
        text: i18n.t('common:ok'),
        handler: () => {
          replace(builder.getDirectConnectSelectMachineRoute());
        },
      },
    ];
  }, [i18n, replace, builder]);

  return (
    <IonAlert
      isOpen={true}
      header={i18n.t('common:direct_connect')}
      message={i18n.t('common:direct_connect_robot_not_found')}
      buttons={buttons}
    />
  );
};

const MachinePages = (props: MachinePageInternalProps) => {
  const { teamId, machineId } = props;
  const { builder } = useBeaconRouter();

  return (
    <AuthorizationProvider>
      <TeamRoleProvider teamId={teamId}>
        <MachineAuthorizationProvider teamId={teamId} machineId={machineId}>
          <MachineContextProvider teamId={teamId} machineId={machineId}>
            <IonRouterOutlet>
              <Page
                path={builder.getMachineRoute(teamId, ':machineId')}
                exact
                i18nTitleKey={'common:page_team_machine'}
              >
                <MachinePageInternal teamId={teamId} machineId={machineId} />
              </Page>
              <Page
                path={builder.getMachineDetailsRoute(teamId, ':id')}
                exact
                i18nTitleKey={'common:page_team_machine'}
              >
                <MachineDetailsPage teamId={teamId} />
              </Page>
              <Page
                path={builder.getMachineScratchpadRoute(
                  teamId,
                  ':machineId',
                  ':id'
                )}
                exact
                i18nTitleKey={'common:page_team_machine'}
              >
                <MachineScratchpadPage teamId={teamId} />
              </Page>
            </IonRouterOutlet>
          </MachineContextProvider>
        </MachineAuthorizationProvider>
      </TeamRoleProvider>
    </AuthorizationProvider>
  );
};

const MachinePageInternal = (props: MachinePageInternalProps) => {
  const { machineId, teamId } = props;

  const swiperRef = useRef<SwiperInterface>();

  const { builder } = useBeaconRouter();
  const { i18n } = useTranslation();
  const { push } = useHistory();

  const hash = useLocationHash();

  const nextTab = hash?.replace('#', '');
  const [topTab, setTopTab] = useState<string | undefined>(nextTab);

  const canCreateScratchpad = useHasPermission('create', 'scratchpad', {
    machineId,
  });

  // register back button listener
  useDirectConnectBackButtonListener();

  // subscribe to single machine state
  useSubscribeToSingleMachineStateSubscription({
    variables: {
      input: {
        machineId: machineId,
      },
    },
  });

  const {
    data: appsData,
    loading,
    error,
    refetch,
  } = useListScratchpadAppsQuery({
    variables: { input: { machineId } },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    onCompleted: () => {
      setReady(true);
    },
  });

  const hasAppData: boolean = appsData?.listScratchpadApps ? true : false;
  const [ready, setReady] = useState<boolean>(hasAppData);

  const appsList = useMemo(
    () => appsData?.listScratchpadApps ?? [],
    [appsData]
  );

  const { data } = useGetMachineQuery({
    variables: { input: { id: machineId, teamId } },
  });

  const machine: Machine | undefined = data?.getMachine;

  const MachineTopTab: Record<string, MachineTopTab> = useMemo(() => {
    return {
      PLAYLIST: {
        hash: 'playlist',
        label: i18n.t('common:machine_tab_playlist'),
        component: (
          <ScratchpadPlaylistComponent machine={machine} teamId={teamId} />
        ),
      },
      PARTS: {
        hash: 'parts',
        label: i18n.t('common:machine_tab_parts'),
        component: <ScratchpadList machine={machine} teamId={teamId} />,
      },
      CONFIGURE: {
        hash: 'config',
        label: i18n.t('common:machine_tab_config'),
        component: (
          <MachineConfig
            machine={machine}
            hideApplications={true}
            hideEvents={true}
            hideFiles={true}
            hideMaintenance={true}
            hideRealtime={true}
          />
        ),
      },
    };
  }, [machine, teamId, i18n]);

  const tabs: MachineTopTab[] = useMemo(() => {
    const tabs = [MachineTopTab.CONFIGURE];

    // conditionally show parts/playlist based on scratchpad type
    if (appsList.length > 0) {
      tabs.unshift(MachineTopTab.PLAYLIST);
      tabs.unshift(MachineTopTab.PARTS);
    }
    return tabs;
  }, [
    MachineTopTab.CONFIGURE,
    MachineTopTab.PARTS,
    MachineTopTab.PLAYLIST,
    appsList.length,
  ]);

  const previousNumTabs = usePrevious(tabs.length);
  const numberTabsChanged = previousNumTabs !== tabs.length;
  const firstTabHash = tabs[0].hash;

  useEffect(() => {
    if (numberTabsChanged) {
      window.location.replace(`${window.location.pathname}#${firstTabHash}`);
    }
  }, [numberTabsChanged, firstTabHash]);

  useEffect(() => {
    const intialTab =
      appsList.length > 0 ? MachineTopTab.PARTS : MachineTopTab.CONFIGURE;
    const tab = topTab ? nextTab : intialTab.hash;
    if (tab && ready) {
      const speed = topTab === tab ? 0 : 250;
      setTopTab(tab);
      const index = tabs.findIndex((t) => t.hash === tab);
      swiperRef.current?.slideTo(index, speed);
    }
  }, [nextTab, appsList.length, ready, tabs, topTab, MachineTopTab]);

  const onChangeSize = useCallback(() => {
    swiperRef.current?.updateAutoHeight(0);
  }, []);

  const createNewPart = (app: ScratchpadApp) => {
    const route = builder.getMachineScratchpadRoute(
      teamId,
      app.machineId,
      'new'
    );

    push(route, {
      app,
    });
  };

  const title = `${machine?.name} - ${i18n.t('common:page_team_machine')}`;

  const scratchpadFabClassList: string[] = ['scratch-pad-fab'];
  if (topTab === MachineTopTab.PARTS.hash) {
    scratchpadFabClassList.push('show-fab');
  }

  const isMachineActive = machine?.active ? true : false;

  const { open: openSupport } = useSupportContext();

  useMachineAlerts(machineId);

  return (
    <>
      <IonPage>
        <DirectConnectPageHeader
          title={title}
          defaultBackRoute={builder.getDirectConnectSelectMachineRoute()}
          secondaryToolbar={
            <IonSegment
              className="top-tabs"
              mode="md"
              scrollable={true}
              swipeGesture={false}
              value={topTab}
            >
              {generateTopTabs(tabs)}
            </IonSegment>
          }
          rightButtons={
            <IonButton
              slot="end"
              onClick={() => {
                openSupport({ source: SupportOpenSource.DIRECT_CONNECT });
              }}
            >
              <IonIcon icon={helpCircleOutline} />
            </IonButton>
          }
        >
          <IonTitle>
            {machine?.name}
            <div className="subtitle header-program-status">
              <MachineProgramStatus machine={machine} />
            </div>
          </IonTitle>
        </DirectConnectPageHeader>
        <IonContentWithContext>
          <LoadingRetry
            loading={loading}
            refetch={refetch}
            error={error}
            results={{ hasResults: ready, noResultsComponent: null }}
          >
            <Swiper
              key={tabs.map((t) => t.hash).join('_')}
              onSwiper={(ref) => (swiperRef.current = ref)}
              modules={[
                Autoplay,
                Keyboard,
                Pagination,
                Scrollbar,
                Zoom,
                IonicSlides,
              ]}
              allowTouchMove={false}
              slidesPerView={1}
              autoHeight={true}
              pagination={false}
            >
              {tabs.map((tab, index) => {
                return (
                  <SwiperSlide key={index}>
                    <SlideContents
                      onChangeSize={onChangeSize}
                      isActive={swiperRef.current?.activeIndex === index}
                    >
                      <>{tab.component}</>
                    </SlideContents>
                  </SwiperSlide>
                );
              })}
            </Swiper>
          </LoadingRetry>
          {canCreateScratchpad && isMachineActive && (
            <IonFab
              className={scratchpadFabClassList.join(' ')}
              vertical="bottom"
              horizontal="end"
              slot="fixed"
              data-cy="scratchpad-create"
            >
              <IonFabButton
                onClick={() => {
                  if (appsList.length === 1) {
                    createNewPart(appsList[0] as ScratchpadApp);
                  }
                }}
              >
                <IonIcon icon={newScratchpad} />
              </IonFabButton>
              {appsList.length > 1 && (
                <IonFabList side="top">
                  {appsList.map((a) => (
                    <IonFabButton
                      key={a.id}
                      onClick={() => {
                        createNewPart(a as ScratchpadApp);
                      }}
                    >
                      <IonIcon icon={ScratchpadAppIcon[a.slug]} />
                    </IonFabButton>
                  ))}
                </IonFabList>
              )}
            </IonFab>
          )}
        </IonContentWithContext>
      </IonPage>
    </>
  );
};

const generateTopTabs = (items: MachineTopTab[]): ReactElement[] => {
  return items.map((tab, index) => {
    return <Tab key={index} tab={tab} />;
  });
};

interface TabContext {
  isActive: boolean;
}

const TabContext = createContext<TabContext>({
  isActive: false,
});

export const useTabContext = (): TabContext => {
  const context = useContext(TabContext);
  return context as TabContext;
};

const Tab = ({ tab }: { tab: MachineTopTab }): ReactElement => {
  const buttonRef = useRef<HTMLIonSegmentButtonElement>(null);
  return (
    <IonSegmentButton
      ref={buttonRef}
      value={tab.hash}
      onClick={() => {
        window.location.replace(`${window.location.pathname}#${tab.hash}`);
        buttonRef.current?.scrollIntoView({ inline: 'end' });
      }}
    >
      {tab.label}
    </IonSegmentButton>
  );
};

interface SlideContentsProps {
  children: ReactNode;
  onChangeSize: () => void;
  isActive: boolean;
}

const SlideContents = (props: SlideContentsProps): ReactElement => {
  const { onChangeSize, children, isActive } = props;
  const priorHeightRef = useRef<number>();
  const [ref, { height }] = useMeasure<HTMLDivElement>();

  useEffect(() => {
    if (priorHeightRef.current !== height) {
      priorHeightRef.current = height;
      onChangeSize();
    }
  }, [height, onChangeSize]);

  const contentContext = useIonContentContext();

  return (
    <TabContext.Provider value={{ isActive }}>
      <div
        ref={ref}
        className={'machine-slide max-content-width'}
        style={{ minHeight: contentContext?.height }}
      >
        {children}
      </div>
    </TabContext.Provider>
  );
};

export { DirectConnectMachinePage };
